import type { IconProps } from '@consta/icons/Icon'
import { IconArrowRight } from '@consta/icons/IconArrowRight'
import { IconFolders } from '@consta/icons/IconFolders'
import { IconForward } from '@consta/icons/IconForward'
import { IconGeo } from '@consta/icons/IconGeo'
import { IconInfo } from '@consta/icons/IconInfo'
import { IconKebab } from '@consta/icons/IconKebab'
import { IconUser } from '@consta/icons/IconUser'
import { Avatar } from '@consta/uikit/Avatar'
import type { Props as ButtonProps } from '@consta/uikit/Button'
import { Button } from '@consta/uikit/Button'
import { Card } from '@consta/uikit/Card'
import { Layout } from '@consta/uikit/Layout'
import { cnMixSpace } from '@consta/uikit/MixSpace'
import type { TextProps } from '@consta/uikit/Text'
import { Text } from '@consta/uikit/Text'
import { ProjectContextMenu } from '@core/components'
import { selectPreferredName, useAppSelector } from '@core/core/store'
import { Show } from '@library/components'
import { createUrlTo } from '@library/model'
import type { Prettify } from '@library/types'
import cn from 'classnames'
import type { Nullable, ProjectId } from 'common/types'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FavoriteButton } from '../../FavoriteButton'
import { ProjectSidebar } from '../../ProjectSidebar'
import { Published } from '../../Published'
import type { ProjectDetailExtended } from '../../types'
import css from './ProjectCard.css'
import { ProjectCardTags } from './ProjectCardTags'
import { useRelativeDate } from './useRelativeDate'

const cnBodyItem = `${cnMixSpace({ mB: 's' })} ${css.alignCenter}`
const bodyClass = `${css.body} ${cnMixSpace({ mB: 's' })}`

const iconProps: IconProps = { size: 's', view: 'ghost' }
const iconsClassname = cnMixSpace({ mR: 'xs' })

const buttonProps: ButtonProps = { size: 's', view: 'clear', onlyIcon: true }
const bodyItemTextProps: TextProps = { size: 's', weight: 'regular', view: 'primary' }

const editPartClass = `${cnMixSpace({ mR: 'xs' })} ${css.alignCenter}`
const headerTextClass = cnMixSpace({ mB: 'm' })
const spaceXsClass = cnMixSpace({ mL: 'xs' })
const bodyItemTextClass = `${cnMixSpace({ mR: 'xs' })} ${css.bodyItemText}`

type ProjectCardProps = Prettify<
  ProjectDetailExtended & { isFavorite: boolean; toggleFavorite: (id: ProjectId) => void }
>

export const ProjectCard: FC<ProjectCardProps> = props => {
  const {
    id,
    name,
    companyName,
    fieldName,
    state,
    tags = [],
    changedAt,
    createdBy,
    isFavorite,
    toggleFavorite,
  } = props
  const changedDate = useRelativeDate(changedAt)

  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const kebabBtnRef = useRef<Nullable<HTMLButtonElement>>(null)
  const userName = useAppSelector(selectPreferredName)

  const isOwned = userName === createdBy

  const onProjectClick = () => {
    navigate(createUrlTo({ base: 'Project', path: id }))
  }
  const closeMenu = () => {
    setIsMenuOpen(false)
  }
  const handleMenu = () => {
    setIsMenuOpen(prev => !prev)
  }
  const handleSidebar = () => {
    setIsSidebarOpen(prev => !prev)
  }
  const handleFavorite = () => {
    toggleFavorite(id)
  }

  return (
    <>
      <Card className={css.container} verticalSpace="m" horizontalSpace="m">
        <Layout className={headerTextClass}>
          <Layout flex={1} className={css.headerTextContainer}>
            <Text
              view="primary"
              size="s"
              as="h3"
              className={css.headerText}
              weight="semibold"
              truncate={true}
              onClick={onProjectClick}>
              {name}
            </Text>
          </Layout>

          <Layout className={css.headerButtons}>
            <Button
              className={css.hidable}
              label="Открыть боковую панель"
              iconLeft={IconInfo}
              onClick={handleSidebar}
              {...buttonProps}
            />

            <Button
              ref={kebabBtnRef}
              className={css.hidable}
              label="Открыть контекстное меню"
              iconLeft={IconKebab}
              onClick={handleMenu}
              {...buttonProps}
            />

            <FavoriteButton
              className={cn({ [css.hidable]: !isFavorite })}
              isFavorite={isFavorite}
              onClick={handleFavorite}
            />
          </Layout>
        </Layout>

        <Layout className={bodyClass} direction="column">
          <Layout className={cnBodyItem}>
            <IconGeo {...iconProps} className={iconsClassname} />
            <Text className={bodyItemTextClass} truncate={true} {...bodyItemTextProps}>
              {companyName}
            </Text>
            <Show when={fieldName}>
              <IconArrowRight {...iconProps} size="xs" className={iconsClassname} />

              <Text className={css.bodyItemText} truncate={true} {...bodyItemTextProps}>
                {fieldName}
              </Text>
            </Show>
          </Layout>

          <Layout className={cnBodyItem}>
            <IconUser {...iconProps} className={iconsClassname} />

            <Text className={css.bodyItemText} {...bodyItemTextProps}>
              {createdBy}
            </Text>
          </Layout>

          <Layout className={cnBodyItem}>
            <IconFolders {...iconProps} className={iconsClassname} />
            <ProjectCardTags items={tags} />
          </Layout>
        </Layout>

        <Layout className={css.editedPart}>
          <Layout flex={1} className={editPartClass}>
            <Text
              className={css.footerStatusText}
              size="s"
              view="secondary"
              weight="regular"
              title="Отредактировано">
              {changedDate}
            </Text>
            <Published state={state} isOwned={isOwned} className={spaceXsClass} />
          </Layout>

          <Layout className={css.footerRight}>
            <Avatar size="s" name={createdBy ?? undefined} />

            <Button
              className={spaceXsClass}
              label="Перейти в проект"
              iconLeft={IconForward}
              onClick={onProjectClick}
              {...buttonProps}
            />
          </Layout>
        </Layout>
      </Card>

      <ProjectContextMenu
        innerRef={kebabBtnRef}
        params={props}
        isContextMenuOpened={isMenuOpen}
        closeMenu={closeMenu}
      />

      <ProjectSidebar
        isSidebarOpen={isSidebarOpen}
        projectData={props}
        closeSidebar={handleSidebar}
      />
    </>
  )
}
